'use client'

import { useEffect, useMemo, useState } from 'react'
import type { MarbleGame } from 'src/types/domain/Game'
import { isRaceStartedEvent } from 'src/types/domain/RaceEvent'
import { isCCTVGameType } from 'src/utils/gameTypeUtils'
import { getCCTVCopy } from 'src/utils/cctvCopy'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import './stream-round-intro.scss'

type Props = Readonly<{
  game: MarbleGame
}>

// Animation timing constants. Cutscene runs ~2-3s after a round
// opens, so the reveal needs to wait that out — otherwise the
// intro pops in over the cutscene fade. Exit fires when betting is
// about to close (race_starting), giving the chip-placement panel
// a clean takeover frame.
const REVEAL_DELAY_FRESH_MS = 2400
const REVEAL_FRESH_WINDOW_MS = 4000
const EXIT_ANIMATION_MS = 300

type Phase = 'visible' | 'exiting' | null

/**
 * Round-open headline overlay for CCTV games. Renders the "HOW
 * MANY X?" question over the live stream during the betting
 * window. The matching disclaimer ("Only X detected by the AI
 * will be counted") is rendered in the chip-placement panel's
 * notice slot — see ChipPlacementPanel's CCTV disclaimer effect.
 *
 * Phase machine, all driven by race events:
 *   race_ready landed     → 'visible' (with reveal animation)
 *   race_starting fired   → 'exiting' (fade out, then unmount)
 *   race_started fired    → null (instant unmount)
 *
 * Reveal animation delays itself by ~2.4s when the round just
 * opened so the cutscene can play; if the user lands mid-betting,
 * the delay is dropped so the headline appears immediately.
 */
export function StreamRoundIntro({ game }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const currentRace = game.races[0]
  const isCCTV = isCCTVGameType(game.track.gameType)

  const livePhase: Phase = useMemo(() => {
    if (!isCCTV || !currentRace) return null
    const events = currentRace.raceEvents
    if (events.some(isRaceStartedEvent)) return null
    if (events.some((e) => e.eventType === 'race_starting')) return 'exiting'
    if (events.some((e) => e.eventType === 'race_ready')) return 'visible'
    return null
  }, [isCCTV, currentRace])

  // `renderPhase` trails `livePhase` so we can play the exit
  // animation before unmounting.
  const [renderPhase, setRenderPhase] = useState<Phase>(null)

  useEffect(() => {
    if (livePhase === renderPhase) return
    if (livePhase === null && renderPhase !== null) {
      setRenderPhase('exiting')
      const t = setTimeout(() => setRenderPhase(null), EXIT_ANIMATION_MS)
      return () => clearTimeout(t)
    }
    setRenderPhase(livePhase)
  }, [livePhase, renderPhase])

  const revealDelayMs = useMemo(() => {
    if (!currentRace) return 0
    const readyEvent = currentRace.raceEvents.find((e) => e.eventType === 'race_ready')
    if (!readyEvent) return 0
    const readyAt = Date.parse(readyEvent.eventTimestamp)
    if (Number.isNaN(readyAt)) return 0
    const ageMs = Date.now() - readyAt
    return ageMs < REVEAL_FRESH_WINDOW_MS ? REVEAL_DELAY_FRESH_MS : 0
  }, [currentRace])

  const copy = useMemo(() => getCCTVCopy(game.track.gameType, t), [game.track.gameType, t])

  if (!renderPhase || !copy) return null

  return (
    <div
      className="stream-round-intro"
      data-phase={renderPhase}
      aria-hidden="true"
      key={currentRace?.uuid ?? 'no-race'}
      style={{
        ['--reveal-delay' as string]: `${revealDelayMs}ms`
      }}
    >
      <h2 className="stream-round-intro__headline">{copy.headline}</h2>
    </div>
  )
}
